import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Title from '../shared/Title';
import Text from '../shared/Text';
import { palette, breakpoints } from '../../constants/styles';
import QuestionByType from '../shared/QuestionInputs/QuestionByType';
import { questionTypes } from '../../constants/questionnaires';
import YesNoQuestion from '../shared/QuestionInputs/YesNoQuestion';

const StyledQuestionBlock = styled.div`
  margin: 60px 0;
  opacity: 0;

  ${({ isAnswered }) =>
    isAnswered &&
    css`
      opacity: 0.6;
    `}

  ${({ noMargin }) =>
    noMargin &&
    css`
      margin: 0px;
    `}

  ${({ isFocused }) =>
    isFocused &&
    css`
      opacity: 1;
    `}
`;

const Container = styled.div`
  margin: 20px 0;
  opacity: 0;
  transition: all 0.2s ease-out;
  pointer-events: none;

  ${({ isTouched }) =>
    isTouched &&
    css`
      opacity: 0.6;
      pointer-events: all;
    `}

  ${({ isFocused }) =>
    isFocused &&
    css`
      opacity: 1;
      font-size: 1.2em;
      pointer-events: all;
    `}
`;

const Question = styled.div`
  color: ${palette.textColor};
`;

const ValidationCheck = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 0px;

  pointer-events: none;
  opacity: 0;
  transition: all 0.3s ease-out;
  visibility: hidden;

  ${({ show }) =>
    show &&
    css`
      height: 50px;
      opacity: 1;
      visibility: visible;
    `}

  ${({ isFocused }) =>
    isFocused &&
    css`
      pointer-events: all;
    `}
`;
const Check = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 25px;
  margin: 0 5px 5px 0;
  padding: 0 10px;

  margin-right: 0.3rem;
  width: fit-content;
  padding: 0px 10px;
  min-height: 40px;
  height: fit-content;
  margin-bottom: 0.3rem;
  border: none;
  border-radius: 8px;
  background-color: ${palette.secondaryColor};
  box-shadow: 2px 2px 15px -3px rgba(0, 0, 0, 0.25);

  color: ${palette.white};
  cursor: pointer;
`;

const StyledSpan = styled.span`
  display: none;
  ${breakpoints.tabletLandscape} {
    font-size: 0.8em;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 50px;
    max-height: 15px;
    margin-right: 0.5em;
    background-color: ${palette.secondaryColorShy};
    padding: 5px;
    border-radius: 8px;
    border-color: ${palette.sweetGray};
    border: 1px solid;
    color: ${palette.sweetGray};
  }
`;

const Reference = styled.div``;

class QuestionBlock extends Component {
  state = { importantKeyIsDown: false };

  componentDidMount = () => {
    document.addEventListener('keyup', this.keyUp, false);
    document.addEventListener('keydown', this.keyDown, false);
  };

  componentWillUnmount() {
    document.removeEventListener('keyup', this.keyUp, false);
    document.removeEventListener('keydown', this.keyDown, false);
  }

  keyDown = (ev) => {
    switch (ev.keyCode) {
      case 65:
      case 97:
      case 66:
      case 98:
      case 67:
      case 99:
      case 68:
      case 100:
      case 83:
      case 115:
      case 78:
      case 110:
        if (!this.state.importantKeyIsDown) {
          this.setState({ importantKeyIsDown: true });
        }
        break;
      default:
        break;
    }
  };

  importantKeyCallback = () => {
    this.setState({ importantKeyIsDown: false });
  };

  checkQuestionValidity = (questionObj) => {
    if (!this.props.form.errors[questionObj.inputName] && questionObj.isFocused) {
      this.props.goToNextInput(`${questionObj.id}`);
    }
  };

  render() {
    const {
      goal,
      goalIndex,
      form,
      isFocused,
      isAnswered,
      noMargin,
      totalQuestions,
      goToNextInput,
      onFocus,
      formIsValid,
      disclaimer,
      ...rest
    } = this.props;
    // const totalAnswers = Object.keys(form.values).map(key => form.values[key]).length;
    const isInputTypeValid = (type) => type === questionTypes.NUMERIC;
    return (
      <StyledQuestionBlock isFocused={isFocused} isAnswered={isAnswered} noMargin={noMargin}>
        {goal.label && (isFocused || isAnswered) && (
          <Title level={3} color={palette.terciaryColor}>
            {goal.label}
          </Title>
        )}

        {goal.questions.map((question, index) => {
          const isQuestionTouched = !!form.touched[question.inputName];
          const isQuestionValid = !form.errors[question.inputName];
          const isQuestionAnswered = isQuestionTouched && isQuestionValid;
          const goalObj = {
            ...goal,
            index: goalIndex,
            isAnswered: isQuestionTouched,
          };

          let questionObj = {};

          if (goal.value !== '') {
            questionObj = {
              ...question,
              id: `${goal.value}-${question.inputName}`,
              index,
            };
          } else {
            questionObj = {
              ...question,
              id: `${question.inputName}`,
              index,
            };
          }

          return (
            <Container
              // eslint-disable-next-line react/no-array-index-key
              key={`${question.inputName}-${index}`}
              isFocused={question.isFocused}
              isTouched={isQuestionTouched}
            >
              <Question>
                <QuestionByType
                  type={question.type}
                  onFocus={onFocus}
                  isFocused={question.isFocused}
                  importantKeyIsDown={this.state.importantKeyIsDown}
                  question={questionObj}
                  goal={goalObj}
                  form={form}
                  importantKeyCallback={
                    question.type === questionTypes.YESNO && this.importantKeyCallback
                  }
                  goToNextInput={goToNextInput}
                  checkQuestionValidity={() => {
                    this.checkQuestionValidity(questionObj);
                  }}
                  {...rest}
                />
              </Question>
              {isQuestionTouched && question.type !== 'select' && (
                <Text type={question.errorType ? question.errorType : 'errorMessage'}>
                  {form.errors[question.inputName]}
                </Text>
              )}
              {question.reference && <Reference>{question.reference}</Reference>}
              <ValidationCheck
                show={
                  (isQuestionAnswered && isInputTypeValid(question.type) && question.isFocused) ||
                  question.final
                }
                isFocused={question.isFocused}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {!question.isTerminal && question.final ? (
                  formIsValid ? (
                    <Check
                      show={isQuestionAnswered && question.isFocused}
                      onClick={() => {
                        if (isQuestionValid) {
                          goToNextInput(questionObj.id);
                        }
                      }}
                    >
                      <Text type="buttonText" color="white" medium>
                        {question.final}
                      </Text>
                    </Check>
                  ) : (
                    <Fragment>
                      <Text type={question.errorType ? question.errorType : 'errorMessage'} medium>
                        Oops! Encontramos errores en una o más de las preguntas anteriores...
                      </Text>
                      {formIsValid}
                      {question.final}
                    </Fragment>
                  )
                ) : (
                  <Check
                    show={isQuestionAnswered && question.isFocused}
                    onClick={() => {
                      if (isQuestionValid) {
                        goToNextInput(questionObj.id);
                      }
                    }}
                  >
                    {question.buttonText ? (
                      question.buttonText
                    ) : (
                      <>
                        <StyledSpan>ENTER</StyledSpan>
                        <Text type="buttonText" color="white" medium>
                          Ok
                        </Text>
                      </>
                    )}
                  </Check>
                )}
                {question.disclaimer && <Reference>{question.disclaimer}</Reference>}
              </ValidationCheck>
            </Container>
          );
        })}
      </StyledQuestionBlock>
    );
  }
}

QuestionBlock.propTypes = {
  goal: PropTypes.shape({
    name: PropTypes.string,
    question: PropTypes.array,
  }),
  form: PropTypes.object,
  goalIndex: PropTypes.number,
  totalQuestions: PropTypes.number,
  isFocused: PropTypes.bool,
  isAnswered: PropTypes.bool,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  goToNextInput: PropTypes.func,
  formIsValid: PropTypes.bool,
  noMargin: PropTypes.bool,
};

QuestionBlock.defaultProps = {
  goal: {},
  form: {},
  goalIndex: 0,
  totalQuestions: 0,
  isFocused: false,
  isAnswered: false,
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {},
  goToNextInput: () => {},
  formIsValid: false,
  noMargin: false,
};

export default QuestionBlock;
